/**
 * Yotpo utility functions for handling review requests
 * Provides robust error handling, retry logic, and request deduplication
 */

// Cache for request deduplication
const yotpoRequestCache = new Map();

const PLP_MIN_AVERAGE_RATING = 4;

const shouldShowPlpRating = (average) => {
    const value = parseFloat(average);
    return Number.isFinite(value) && value >= PLP_MIN_AVERAGE_RATING;
};

const parseYotpoRatingFromHtml = (html) => {
    if (!html || typeof html !== 'string') {
        return null;
    }

    const patterns = [
        /data-score=["']([\d.]+)["']/i,
        /([\d.]+)\s*star rating/i,
    ];

    for (const pattern of patterns) {
        const match = html.match(pattern);
        if (match) {
            const value = parseFloat(match[1]);
            if (Number.isFinite(value)) {
                return value;
            }
        }
    }

    return null;
};

const parseYotpoBottomlineRating = (root) => {
    if (!root) {
        return null;
    }

    const container = root.classList?.contains('plpRatings')
        ? root
        : root.querySelector?.('.plpRatings') || root;
    const bottomline = container.querySelector?.(
        '.yotpo-bottomline, .standalone-bottomline, .yotpo.bottomLine'
    );

    if (!bottomline) {
        return null;
    }

    const scoreElement = bottomline.querySelector('[data-score]');
    if (scoreElement?.dataset?.score) {
        const value = parseFloat(scoreElement.dataset.score);
        if (Number.isFinite(value)) {
            return value;
        }
    }

    if (bottomline.dataset?.score) {
        const value = parseFloat(bottomline.dataset.score);
        if (Number.isFinite(value)) {
            return value;
        }
    }

    const screenReaderText = bottomline.querySelector(
        '.sr-only, .yotpo-sr-only-only-no-review, [class*="sr-only"]'
    );
    if (screenReaderText?.textContent) {
        const match = screenReaderText.textContent.match(/([\d.]+)/);
        if (match) {
            const value = parseFloat(match[1]);
            if (Number.isFinite(value)) {
                return value;
            }
        }
    }

    const stars = bottomline.querySelectorAll('.yotpo-icon-star, .yotpo-icon-half-star');
    if (stars.length) {
        let score = 0;
        stars.forEach((star) => {
            if (star.classList.contains('yotpo-icon-star')) {
                score += 1;
            } else if (star.classList.contains('yotpo-icon-half-star')) {
                score += 0.5;
            }
        });

        if (score > 0) {
            return score;
        }
    }

    return null;
};

const prunePlpRatingsElement = (plpRatings) => {
    if (!plpRatings) {
        return;
    }

    const starRating = plpRatings.querySelector('.star-rating');
    if (starRating) {
        const style = starRating.getAttribute('style') || '';
        const match = style.match(/--star-rating:\s*([\d.]+)/);
        const average = match ? parseFloat(match[1]) : null;

        if (!shouldShowPlpRating(average)) {
            plpRatings.remove();
        }
        return;
    }

    const yotpoStar = plpRatings.querySelector(
        '.yotpo .yotpo-bottomline .yotpo-icon-star, .yotpo .standalone-bottomline .yotpo-icon-star'
    );
    if (!yotpoStar) {
        plpRatings.remove();
        return;
    }

    const average = parseYotpoBottomlineRating(plpRatings);
    if (average == null || !shouldShowPlpRating(average)) {
        plpRatings.remove();
    }
};

const pruneLowPlpRatings = (root = document) => {
    root.querySelectorAll('.productInfoRight .plpRatings').forEach(prunePlpRatingsElement);
};

let plpRatingsObserverStarted = false;

const observePlpRatings = () => {
    if (plpRatingsObserverStarted || typeof document === 'undefined') {
        return;
    }

    plpRatingsObserverStarted = true;

    let pruneTimer;
    const schedulePrune = () => {
        clearTimeout(pruneTimer);
        pruneTimer = window.setTimeout(() => pruneLowPlpRatings(), 100);
    };

    const ratingsObserver = new MutationObserver(() => {
        schedulePrune();
    });

    const observeRatingsContainer = (element) => {
        if (!element || element.dataset?.plpRatingsObserved === '1') {
            return;
        }

        element.dataset.plpRatingsObserved = '1';
        ratingsObserver.observe(element, { childList: true, subtree: true });
    };

    document.querySelectorAll('.plpRatings').forEach(observeRatingsContainer);

    const gridObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof Element)) {
                    return;
                }

                if (node.matches('.plpRatings')) {
                    observeRatingsContainer(node);
                }

                node.querySelectorAll('.plpRatings').forEach(observeRatingsContainer);
            });
        });
        schedulePrune();
    });

    const grid = document.querySelector('#ProductGridContainer, #algolia-shopify-instantsearch');
    if (grid) {
        gridObserver.observe(grid, { childList: true, subtree: true });
    }

    schedulePrune();
    window.setTimeout(schedulePrune, 1500);
    window.setTimeout(schedulePrune, 3000);
};

/**
 * Resolve Yotpo product id from API/search product data.
 * Uses custom.production_product_id when present, otherwise Shopify product id.
 * @param {Object} product
 * @returns {string}
 */
const getYotpoProductId = (product) => {
    if (!product) {
        return '';
    }

    const metaId =
        product.mfield_custom_production_product_id ??
        product['mfield_custom_production_product_id'] ??
        product.meta?.custom?.production_product_id ??
        product.metafields?.custom?.production_product_id;

    if (metaId != null && metaId !== '') {
        return String(metaId);
    }

    if (product.uid != null && product.uid !== '') {
        return String(product.uid);
    }

    if (product.id != null && product.id !== '') {
        return String(product.id);
    }

    return '';
};

/**
 * Apply production_product_id fallback to Yotpo widgets in the DOM.
 * @param {ParentNode} [root]
 */
const patchYotpoProductIds = (root = document) => {
    root.querySelectorAll(
        '.yotpo-widget-instance, .yotpo.yotpo-main-widget, .yotpo.bottomLine, [data-yotpo-instance-id]'
    ).forEach((element) => {
        const card = element.closest('product-card');
        const resolvedId =
            window.__natoriYotpoProductId ||
            card?.dataset?.yotpoProductId ||
            element.dataset.productionProductId ||
            element.dataset.yotpoProductId ||
            element.dataset.productId;

        if (!resolvedId) {
            return;
        }

        element.dataset.yotpoProductId = resolvedId;
        element.dataset.productId = resolvedId;

        if ('productModels' in element.dataset) {
            element.dataset.productModels = resolvedId;
        }
    });
};

const scheduleYotpoProductIdPatch = () => {
    patchYotpoProductIds();

    if (window.yotpo?.refreshWidgets) {
        try {
            window.yotpo.refreshWidgets();
        } catch (error) {
            console.warn('Yotpo refreshWidgets failed after product id patch', error);
        }
    }
};

const scheduleYotpoProductIdPatchWithRetry = (attempt = 0) => {
    scheduleYotpoProductIdPatch();

    if (!window.__natoriYotpoProductId || window.yotpo?.refreshWidgets || attempt >= 10) {
        return;
    }

    window.setTimeout(() => scheduleYotpoProductIdPatchWithRetry(attempt + 1), 250);
};

if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        scheduleYotpoProductIdPatchWithRetry();
        observePlpRatings();
    });
    document.addEventListener('shopify:section:load', (event) => {
        scheduleYotpoProductIdPatchWithRetry();
        patchYotpoProductIds(event.target);
        pruneLowPlpRatings(event.target);
    });
}

/**
 * Send a Yotpo request with robust error handling, retry logic, and deduplication
 * @param {string} prodId - Product ID
 * @param {string} prodUrl - Product URL
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise<Object|null>} Yotpo widget data or null on failure
 */
const sendYotpoRequest = async (prodId, prodUrl, retryCount = 0) => {
    // Check if request is already in progress
    const cacheKey = `${prodId}_${prodUrl}`;
    if (yotpoRequestCache.has(cacheKey)) {
        return yotpoRequestCache.get(cacheKey);
    }

    // Validate required parameters
    if (!prodId || !prodUrl) {
        console.warn('sendYotpoRequest: Missing required parameters', { prodId, prodUrl });
        return null;
    }

    // Check if yotpo object exists
    if (!window.yotpo || !window.yotpo.appKey) {
        console.warn('sendYotpoRequest: Yotpo not initialized');
        return null;
    }

    const yotpoKey = window.yotpo.appKey;
    const yotpoVersion = window.yotpo.userSettings?.version || '1.0';
    
    const data = {
        methods: [
            {
                method: "bottomline",
                params: {
                    pid: prodId,
                    link: prodUrl,
                    skip_average_score: true,
                    widget_product_id: prodId 
                }
            }
        ],
        app_key: yotpoKey,
        is_mobile: false,
        widget_version: yotpoVersion
    };
    
    const yotpoUrl = `https://staticw2.yotpo.com/batch/app_key/${yotpoKey}/domain_key/${prodId}/widget/bottomline`;
    
    // Create a promise and cache it
    const requestPromise = (async () => {
        try {
            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            const response = await fetch(yotpoUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
            // Remove from cache after successful completion
            yotpoRequestCache.delete(cacheKey);
            
            return result;
        } catch (error) {
            // Remove from cache on error
            yotpoRequestCache.delete(cacheKey);
            
            if (error.name === 'AbortError') {
                console.warn(`sendYotpoRequest: Request timeout for product ${prodId}`);
            } else {
                console.error(`sendYotpoRequest: Request failed for product ${prodId}:`, error);
            }
            
            // Retry logic (max 2 retries)
            if (retryCount < 2) {
                console.log(`sendYotpoRequest: Retrying request for product ${prodId} (attempt ${retryCount + 1})`);
                await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
                return sendYotpoRequest(prodId, prodUrl, retryCount + 1);
            }
            
            return null;
        }
    })();
    
    // Cache the promise
    yotpoRequestCache.set(cacheKey, requestPromise);
    
    return requestPromise;
};

/**
 * Process Yotpo reviews for multiple product cards with improved error handling
 * @param {NodeList} productCards - Collection of product card elements
 * @returns {Promise<void>}
 */
const processYotpoReviews = async (productCards) => {
    const reviewPromises = [];
    
    for (const productCard of productCards) {
        const productBottomLine = productCard.querySelector('.product-card-info .productInfoRight .plpRatings .yotpo.bottomLine');
        const prodUrl = productCard.querySelector('.product-featured-image-link')?.href;
        
        if (productBottomLine && prodUrl) {
            const prodId = productBottomLine.getAttribute('data-product-id');
            
            if (prodId) {
                // Add request to promises array for parallel processing
                reviewPromises.push(
                    sendYotpoRequest(prodId, prodUrl)
                        .then(yotpoWidget => {
                            if (yotpoWidget && yotpoWidget[0] && yotpoWidget[0].result) {
                                const yotpoReviews = yotpoWidget[0].result;
                                const plpRatings = productBottomLine.closest('.plpRatings');
                                const average =
                                    parseYotpoRatingFromHtml(yotpoReviews) ??
                                    yotpoWidget[0].average_score ??
                                    yotpoWidget[0].averageScore;

                                if (!shouldShowPlpRating(average)) {
                                    plpRatings?.remove();
                                    return { success: true, prodId, skipped: true };
                                }

                                productBottomLine.innerHTML = yotpoReviews;
                                prunePlpRatingsElement(plpRatings);
                                return { success: true, prodId };
                            } else {
                                console.warn(`sendYotpoRequest: No valid data returned for product ${prodId}`);
                                return { success: false, prodId, error: 'No valid data' };
                            }
                        })
                        .catch(error => {
                            console.warn(`sendYotpoRequest: Failed to load reviews for product ${prodId}:`, error);
                            return { success: false, prodId, error: error.message };
                        })
                );
            } else {
                console.warn('sendYotpoRequest: No product ID found for element');
            }
        }
    }
    
    // Wait for all requests to complete
    try {
        const results = await Promise.allSettled(reviewPromises);
        const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;
        
        console.log(`Yotpo reviews: ${successful} successful, ${failed} failed`);
        pruneLowPlpRatings();
    } catch (error) {
        console.error('Error processing Yotpo reviews:', error);
    }
};

// Export functions for use in other files
if (typeof module !== 'undefined' && module.exports) {
    // Node.js environment
    module.exports = {
        sendYotpoRequest,
        processYotpoReviews,
        getYotpoProductId,
        patchYotpoProductIds,
        shouldShowPlpRating,
        pruneLowPlpRatings,
        prunePlpRatingsElement,
    };
} else {
    // Browser environment
    window.YotpoUtils = {
        sendYotpoRequest,
        processYotpoReviews,
        getYotpoProductId,
        patchYotpoProductIds,
        shouldShowPlpRating,
        pruneLowPlpRatings,
        prunePlpRatingsElement,
    };
    window.getYotpoProductId = getYotpoProductId;
}
